<script setup lang="ts">
import { computed } from 'vue'
import type { ImportacaoStatus } from '@/types'

interface Props {
  processados: number
  total: number
  status: ImportacaoStatus
}

const props = defineProps<Props>()

// Sem percentual definido enquanto valida (local/API) ou aguarda na fila
const indeterminado = computed(() => props.status === 'validating' || props.status === 'queued')

const emProcessamento = computed(
  () => props.status === 'processing' || props.status === 'importing'
)

const percentual = computed(() => {
  if (props.total === 0) return 0
  return Math.round((props.processados / props.total) * 100)
})

const statusColor = computed(() => {
  switch (props.status) {
    case 'completed':
      return 'success'
    case 'error':
      return 'error'
    default:
      return 'primary'
  }
})

const statusIcon = computed(() => {
  switch (props.status) {
    case 'queued':
      return 'mdi-clock-outline'
    case 'processing':
    case 'importing':
      return 'mdi-loading'
    case 'completed':
      return 'mdi-check-circle'
    case 'error':
      return 'mdi-alert-circle'
    default:
      return 'mdi-progress-clock'
  }
})

const statusText = computed(() => {
  switch (props.status) {
    case 'validating':
      return 'Validando dados...'
    case 'queued':
      return 'Na fila, aguardando processamento...'
    case 'processing':
    case 'importing':
      return `Importando... ${props.processados} de ${props.total}`
    case 'completed':
      return 'Importação concluída!'
    case 'error':
      return 'Erro na importação'
    default:
      return 'Processando...'
  }
})
</script>

<template>
  <v-card
    v-if="['validating', 'queued', 'processing', 'importing'].includes(status)"
    class="mb-4"
    variant="outlined"
  >
    <v-card-title class="text-subtitle-1 font-weight-medium">
      <v-icon start size="small" :class="{ 'mdi-spin': emProcessamento }">
        {{ statusIcon }}
      </v-icon>
      Progresso
    </v-card-title>

    <v-card-text>
      <div class="d-flex align-center justify-space-between mb-2">
        <span class="text-body-2">{{ statusText }}</span>
        <span v-if="!indeterminado" class="text-body-2 font-weight-medium">{{ percentual }}%</span>
      </div>

      <v-progress-linear
        :model-value="percentual"
        :color="statusColor"
        height="12"
        rounded
        :indeterminate="indeterminado"
      />

      <div v-if="emProcessamento" class="text-caption text-grey mt-2 text-center">
        Não feche esta página durante a importação
      </div>
    </v-card-text>
  </v-card>
</template>
